import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {toast} from "react-toastify";

import css from './PromoCodesCreatePromoCodeModal.module.css';
import {promoCodesService} from "../../../../services/settings-services";
import {packsActions, periodsActions, promoCodesActions} from "../../../../store/slices/settings-slices";


const PromoCodesCreatePromoCodeModal = ({setIsOpen}) => {
    const dispatch = useDispatch();
    const [isShow, setIsShow] = useState(false);
    const {t, i18n} = useTranslation();

    const {packs} = useSelector(store => store.packs);
    const {periods} = useSelector(store => store.periods);

    // matrix state: { [packId]: { [periodId]: { discountType: string, amount: number } } }
    const [matrix, setMatrix] = useState({});

    useEffect(() => {
        dispatch(packsActions.getAllPacks());
        dispatch(periodsActions.getAllPeriods());
    }, [dispatch]);

    const packHeaders = useMemo(() => {
        const lang = i18n.language;
        return (packs || []).map(p => ({
            id: p.id,
            name: (p.name && (p.name[lang] || p.name["en"])) || String(p.id)
        }));
    }, [packs, i18n.language]);

    const handleCellChange = (packId, periodId, field, value) => {
        setMatrix(prev => {
            const byPack = prev[packId] || {};
            const cell = byPack[periodId] || {discountType: 'Percentage', amount: 0};
            const next = {
                ...prev,
                [packId]: {
                    ...byPack,
                    [periodId]: {
                        ...cell,
                        [field]: field === 'amount' ? (value === '' ? '' : Number(value)) : value
                    }
                }
            };
            return next;
        });
    };

    const {
        register,
        handleSubmit,
        formState: {errors},
    } = useForm();

    const submitHandler = async (data) => {
        try {
            const fromDate = new Date(data.validFrom);
            const toDate = new Date(data.validTo);
            if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
                toast.error('Please provide valid dates');
                return;
            }
            if (fromDate > toDate) {
                toast.error('"Valid from" must be before or equal to "Valid to"');
                return;
            }

            const formattedData = {
                promoCode: data.promoCode,
                description: {
                    en: data.descriptionEn || "",
                    ru: data.descriptionRu || "",
                    ge: data.descriptionKa || "",
                },
                staffDescription: data.staffDescription || "",
                discountType: data.discountType || "Amount",
                validFrom: fromDate.toISOString(),
                validUntil: toDate.toISOString(),
                appendDiscount: !!data.appendDiscount,
                firstOrderOnly: !!data.firstOrder,
                oneTimeUse: !!data.oneTime,
                amount: Number(data.amount) || 0,
            };

            if (isShow) {
                formattedData.promoMode = 'TABLE';
                // root amount still required by DTO; set 0 for table mode
                formattedData.amount = 0;

                const rules = [];
                (packs || []).forEach(p => {
                    (periods || []).forEach(per => {
                        const cell = matrix?.[p.id]?.[per.id];
                        const type = cell?.discountType;
                        const value = cell?.amount;
                        if (!type) return;

                        const rule = {
                            packId: p.id,
                            periodId: per.id,
                            discountType: type,
                        };

                        if (type === 'Percentage') {
                            if (value !== undefined && value !== '' && Number(value) > 0) {
                                rule.percent = Number(value);
                                rules.push(rule);
                            }
                        } else if (type === 'Amount' || type === 'FreeDays' || type === 'AdditionalDays') {
                            if (value !== undefined && value !== '' && Number(value) > 0) {
                                rule.amount = Number(value);
                                rules.push(rule);
                            }
                        } else if (type === 'FreeDelivery') {
                            // no numeric value needed
                            rules.push(rule);
                        }
                    });
                });
                formattedData.promoTableRules = rules;
            } else {
                formattedData.promoMode = 'SIMPLE';
            }

            await promoCodesService.createPromoCodes(formattedData);
            dispatch(promoCodesActions.changeTrigger());
            toast.success('successful');
            setIsOpen(false);
        } catch (error) {
            console.error(t('modal-toast-creating-error'), error);
            toast.error(t('modal-toast-creating-error'));
        }
    };


    return (
        <section className={css.modalOverlay}>
            <div className={css.modal}>
                <button className={css.closeButton} onClick={() => setIsOpen(false)}>
                    &times;
                </button>
                <h2 className={css.title}>{t('modal-promo-codes-new-promo-code')}</h2>

                <form onSubmit={handleSubmit(submitHandler)}>
                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="promoCode">{t('Promo-code')}</label>
                            <input
                                type="text"
                                id="promoCode"
                                {...register("promoCode", {required: "Promo Code is required"})}
                                className={errors.promoCode ? css.inputError : ""}
                            />
                            {errors.promoCode && (
                                <p className={css.error}>{errors.promoCode.message}</p>
                            )}
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="staffDescription">{t('Staff-description')}</label>
                            <input
                                type="text"
                                id="staffDescription"
                                {...register("staffDescription")}
                            />
                        </div>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="validFrom">{t('Valid-from')}</label>
                            <input
                                type="date"
                                id="validFrom"
                                {...register("validFrom", {required: "Valid From is required"})}
                            />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="validTo">{t('Valid-to')}</label>
                            <input
                                type="date"
                                id="validTo"
                                {...register("validTo", {required: "Valid To is required"})}
                            />
                        </div>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionEn">{t('Client-description')} (English)</label>
                            <input type="text" id="descriptionEn" {...register("descriptionEn")} />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionRu">{t('Client-description')} (Русский)</label>
                            <input type="text" id="descriptionRu" {...register("descriptionRu")} />
                        </div>
                        <div className={css.inputGroup}>
                            <label htmlFor="descriptionKa">{t('Client-description')} (ქართული)</label>
                            <input type="text" id="descriptionKa" {...register("descriptionKa")} />
                        </div>
                    </div>

                    <div className={css.checkboxGroup}>
                        <label>
                            <input type="checkbox" {...register("appendDiscount")} />
                            <span className={css.nameCheckbox}>{t('Append-discount')}</span>
                        </label>
                        <label>
                            <input type="checkbox" {...register("firstOrder")} />
                            <span className={css.nameCheckbox}>{t('First-order')}</span>
                        </label>
                        <label>
                            <input type="checkbox" {...register("oneTime")} />
                            <span className={css.nameCheckbox}>{t('One-time')}</span>
                        </label>
                    </div>

                    <div className={css.row}>
                        <div className={css.inputGroup}>
                            <label htmlFor="discountType">{t('Discount-type')}</label>
                            <select id="discountType"
                                    {...register("discountType")}
                                    className={css.selectDiscount}
                            >
                                <option value="Percentage">{t('Percentage')}</option>
                                <option value="Amount">{t('Amount')}</option>
                                <option value="FreeDelivery">{t('Free-delivery')}</option>
                                <option value="FreeDays">{t('Free-days')}</option>
                                <option value="AdditionalDays">{t('Additional-days')}</option>
                            </select>
                        </div>

                        <div className={`${css.inputGroup} ${css.block}`}>
                            <label>{t('Type')}</label>
                            <div className={css.buttonGroup}>
                                <button type="button"
                                        className={`${css.btn} ${!isShow ? css.buttonActive : ''}`}
                                        onClick={() => setIsShow(false)}>
                                    {t('Simple')}
                                </button>
                                <button type="button"
                                        className={`${css.btn} ${isShow ? css.buttonActive : ''}`}
                                        onClick={() => setIsShow(true)}>
                                    {t('Table')}
                                </button>
                            </div>
                        </div>

                        <div className={`${css.inputGroup} ${isShow ? css.none : ''}`}>
                            <label htmlFor="amount">{t('Amount')}</label>
                            <input
                                type="number"
                                id="amount"
                                {...register("amount")}
                                defaultValue={0}
                            />
                        </div>
                    </div>

                    {isShow && (
                        <div className={css.row}>
                            <div className={css.inputGroup} style={{width: '100%', overflowX: 'auto'}}>
                                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                                    <thead>
                                    <tr>
                                        <th style={{textAlign: 'left', padding: '8px 4px'}}>{t('Periods')}</th>
                                        {packHeaders.map(ph => (
                                            <th key={ph.id} style={{textAlign: 'left', padding: '8px 4px'}}>{ph.name}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(periods || []).map(per => (
                                        <tr key={per.id}>
                                            <td style={{padding: '6px 4px'}}>{per.daysCount} {t('Days-count')}</td>
                                            {packHeaders.map(ph => {
                                                const cell = matrix?.[ph.id]?.[per.id] || {discountType: 'Percentage', amount: 0};
                                                return (
                                                    <td key={`${ph.id}-${per.id}`} style={{padding: '6px 4px'}}>
                                                        <div style={{display: 'flex', gap: '6px'}}>
                                                            <select
                                                                style={{width: 100}}
                                                                value={cell.discountType}
                                                                onChange={(e) => handleCellChange(ph.id, per.id, 'discountType', e.target.value)}
                                                            >
                                                                <option value="Percentage">{t('Percentage')}</option>
                                                                <option value="Amount">{t('Amount')}</option>
                                                                <option value="FreeDelivery">{t('Free-delivery')}</option>
                                                                <option value="FreeDays">{t('Free-days')}</option>
                                                                <option value="AdditionalDays">{t('Additional-days')}</option>
                                                            </select>
                                                            <input
                                                                type="number"
                                                                value={cell.amount === '' ? '' : cell.amount}
                                                                onChange={(e) => handleCellChange(ph.id, per.id, 'amount', e.target.value)}
                                                                style={{width: 90}}
                                                            />
                                                        </div>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <button type="submit" className={css.submitButton}>
                        Submit
                    </button>
                </form>
            </div>
        </section>
    );
};

export {PromoCodesCreatePromoCodeModal};
